import './UserManagement.scss'

import { useEffect, useMemo, useState } from 'react'

import { ToastService } from '../../services/toast.service'
import type { CustomerUserRoleItem, User } from '../../services/usersManagement'
import { createUser, fetchCustomerUserRoles,fetchUserById, updateCustomerUserRoles, updateUser } from '../../services/usersManagement'
import AssignUserTab from './components/AssignUserTab'
import UserInformationTab from './components/UserInformationTab'
import UserTypeTab from './components/UserTypeTab'
import { useAssignUserTab } from './hooks/useAssignUserTab'
import { useUserManagementForm } from './hooks/useUserManagementForm'
import { useUserManagementRules } from './hooks/useUserManagementRules'
import { fetchAllUsers } from './service/userManagement.service'
import { parseConfig,type UserManagementProps } from './UserManagement.config'
import { USER_MANAGEMENT_DEFAULTS as D } from './UserManagement.constants'

type Tab3Assignment = {
    customerId: string
    customerLabel: string
    roleId: string
    roleLabel: string
    productIds: string[]
    productLabels: string[]
}

function getInfoTabButtonLabel(options: {
    submitFromInfoTab: boolean
    isEditMode: boolean
    submitLabel?: string
    nextLabel?: string
}): string {
    if (!options.submitFromInfoTab) return options.nextLabel ?? D.BTN_NEXT
    if (options.submitLabel) return options.submitLabel
    return options.isEditMode ? D.BTN_SAVE_CHANGES : D.BTN_ADD_USER
}

// Component complexity reflects the multi-step form orchestration and conditional tab flow.
// eslint-disable-next-line sonarjs/cognitive-complexity
const UserManagement = (props: UserManagementProps) => {
    const rawConfig = props.configJson || props['config-json'] || ''
    const config = useMemo(() => parseConfig(rawConfig), [rawConfig])

    const actionType = config.actionType ?? props.actionType ?? props['action-type'] ?? D.ACTION_TYPE_ADD
    const cancelRedirectPath = config.confirmation?.cancelRedirectPath ?? props.cancelRedirectPath ?? props['cancel-redirect-path']
    const isEditMode = actionType === D.ACTION_TYPE_EDIT

    const tabLabels = [
        config.userType?.tabLabel ?? D.TAB_USER_TYPE,
        config.userInformation?.tabLabel ?? D.TAB_USER_INFORMATION,
        config.assignAccounts?.tabLabel ?? D.TAB_ASSIGN_USER,
    ]

    // extract user ID from ?id= query param or last numeric URL path segment
    const userId = useMemo(() => {
        const fromSearch = new URLSearchParams(window.location.search).get('id')
        if (fromSearch) return fromSearch
        const segments = window.location.pathname.split('/')
        const last = segments.at(-1)?.replace(/\.html$/, '')
        return /^\d+$/.test(last ?? '') ? last : undefined
    }, [])
    const [activeTab, setActiveTab] = useState(isEditMode ? 1 : 0)
    // highest tab index the user has validly navigated to — in edit mode all tabs start
    // unlocked (data already exists); tab3Disabled still gates admin/manager user types
    const [unlockedUpTo, setUnlockedUpTo] = useState(isEditMode ? 2 : 0)
    const [submitting, setSubmitting] = useState(false)
    const [initialRoles, setInitialRoles] = useState<CustomerUserRoleItem[]>([])
    const [tab3Assignments, setTab3Assignments] = useState<Tab3Assignment[]>([])
    const [allUsers, setAllUsers] = useState<User[]>([])
    const { formData, update, reset, prefill } = useUserManagementForm()
    const isManagerType = formData.userType === 'manager'
    // load dropdown data once on mount so it's ready before the user reaches Tab 3
    const { customerOptions, roleOptions, productOptions, loading: optionsLoading, searchingCustomers, loadingMoreCustomers, hasMoreCustomers, setCustomerSearchTerm, loadMoreCustomers } = useAssignUserTab()
    const { hideProductDropdown, disabledRoleValues, tab3Disabled, isLoggedInAsUserManager, noSlaCustomerValue } = useUserManagementRules(formData, customerOptions, roleOptions)
    const submitFromInfoTab = formData.userType === D.USER_TYPE_MANAGER || formData.userType === D.USER_TYPE_ADMIN || (isEditMode && tab3Disabled)
    const infoTabButtonLabel = getInfoTabButtonLabel({
        submitFromInfoTab,
        isEditMode,
        submitLabel: config.submitLabel,
        nextLabel: config.nextLabel,
    })

    // true when an admin edits their own account — restricts editable fields to name + function only
    const isSelfEdit = useMemo(() => {
        if (!isEditMode || !userId) return false
        try {
            const raw = sessionStorage.getItem(D.SESSION_KEY_CURRENT_USER)
            if (!raw) return false
            const user = JSON.parse(raw) as { id?: number }
            return String(user?.id) === String(userId)
        } catch { return false }
    }, [isEditMode, userId])

    // load all users once for client-side email duplicate validation
    useEffect(() => {
        if ((window as unknown as { Granite?: { author?: unknown } }).Granite?.author) return
        fetchAllUsers().then(setAllUsers)
    }, [])

    // Auto-select NOSLA for logged-in User Managers and for manager-type users being created/edited
    useEffect(() => {
        if ((isLoggedInAsUserManager || isManagerType) && noSlaCustomerValue) {
            update('selectedCustomer', noSlaCustomerValue)
        }
    }, [isLoggedInAsUserManager, isManagerType, noSlaCustomerValue])

    // in edit mode: fetch user details AND existing role assignments in parallel
    useEffect(() => {
        if (!isEditMode || !userId) return
        if ((window as unknown as { Granite?: { author?: unknown } }).Granite?.author) return
        Promise.all([fetchUserById(userId), fetchCustomerUserRoles(userId)])
            .then(([user, roles]) => {
                if (user) prefill(user)
                else ToastService.error(D.TOAST_ERROR_LOAD_USER)
                setInitialRoles(roles)
            })
    }, [isEditMode, userId])


    const navigateNext = (targetTab: number) => {
        setActiveTab(targetTab)
        if (targetTab > unlockedUpTo) setUnlockedUpTo(targetTab)
    }

    const handleInfoTabNext = () => {
        navigateNext(2)
    }

    const handleInfoTabAction = () => {
        if (submitFromInfoTab) {
            void handleAddUser()
            return
        }
        handleInfoTabNext()
    }

    const handleTabChange = (nextTab: number) => {
        if (nextTab === 2 && (tab3Disabled || submitFromInfoTab)) return
        if (nextTab === 2 && activeTab === 1) {
            setActiveTab(2)
            setUnlockedUpTo(prev => Math.max(prev, 2))
            return
        }
        setActiveTab(nextTab)
        if (nextTab > unlockedUpTo) setUnlockedUpTo(nextTab)
    }

    const handleCancel = () => {
        reset()
        setUnlockedUpTo(0)
        if (cancelRedirectPath) window.location.href = cancelRedirectPath
        else window.history.back()
    }

    const assignRolesAfterCreate = async (newUserId: string | number, roles: CustomerUserRoleItem[], syncEmptyRoles = false) => {
        if ((!syncEmptyRoles && roles.length === 0) || !newUserId) return
        try {
            await updateCustomerUserRoles(newUserId, roles)
            ToastService.success(D.TOAST_SUCCESS_UPDATE_ROLES)
        } catch {
            ToastService.error(D.TOAST_ERROR_UPDATE_ROLES)
        }
    }

    const handleAddUser = async (customerUserRoles: CustomerUserRoleItem[] = []) => {
        setSubmitting(true)
        const confirmation = config.confirmation
        const payload = {
            cspAccess: formData.cspAccess,
            email: formData.email.trim().toLowerCase(),
            firstName: formData.firstName,
            function: formData.func,
            isGAShieldUser: formData.isGAShieldUser,
            isMultiTenantUser: formData.isMultiTenantUser,
            isSystemAdmin: formData.userType === D.USER_TYPE_ADMIN,
            isUserManager: formData.userType === D.USER_TYPE_MANAGER,
            lastname: formData.lastName,
            partnerPortalAccess: formData.partnerPortalAccess,
            products: formData.selectedProducts,
            psaAccess: formData.psaAccess,
        }
        try {
            if (isEditMode && userId) {
                await updateUser(userId, payload)
                ToastService.success(
                    confirmation?.successDescription ?? D.TOAST_SUCCESS_UPDATE_USER,
                    confirmation?.successTitle
                )
                // always call customeruserroles in edit mode to sync assignments
                await updateCustomerUserRoles(userId, customerUserRoles)
                ToastService.success(D.TOAST_SUCCESS_UPDATE_ROLES)
            } else {
                const newUser = await createUser(payload)
                ToastService.success(
                    confirmation?.successDescription ?? D.TOAST_SUCCESS_CREATE_USER,
                    confirmation?.successTitle
                )
                await assignRolesAfterCreate(
                    newUser.id,
                    customerUserRoles,
                    formData.userType === D.USER_TYPE_ADMIN,
                )
            }
            if (confirmation?.successRedirectPath) {
                // redirect without resetting UI so the current tab stays visible during the delay
                setTimeout(() => { window.location.href = confirmation.successRedirectPath! }, 3000)
            } else {
                reset()
                setActiveTab(0)
                setUnlockedUpTo(0)
            }
        } catch {
            ToastService.error(
                confirmation?.errorDescription ?? (isEditMode ? D.TOAST_ERROR_UPDATE_USER : D.TOAST_ERROR_CREATE_USER),
                confirmation?.errorTitle
            )
        } finally {
            setSubmitting(false)
        }
    }
    return (
        <div className="subscription-modal">
            <div className="um-container">
            <nav className="um-tabs" role="tablist">
                {tabLabels.map((label, index) => {
                    const isLocked = index > unlockedUpTo || (index === 2 && (tab3Disabled || submitFromInfoTab))
                    return (
                        <button
                            key={label}
                            role="tab"
                            aria-selected={activeTab === index}
                            aria-disabled={isLocked}
                            disabled={isLocked}
                            className={`um-tab${activeTab === index ? ' um-tab--active' : ''}${isLocked ? ' um-tab--locked' : ''}`}
                            onClick={() => !isLocked && handleTabChange(index)}
                        >
                            {label}
                        </button>
                    )
                })}
            </nav>

            {activeTab === 0 && (
                <UserTypeTab
                    onNext={() => navigateNext(1)}
                    onCancel={handleCancel}
                    formData={formData}
                    update={update}
                    isLoggedInAsUserManager={isLoggedInAsUserManager}
                    isSelfEdit={isSelfEdit}
                    config={config}
                />
            )}
            {activeTab === 1 && (
                <UserInformationTab
                    onNext={handleInfoTabAction}
                    onCancel={handleCancel}
                    isEditMode={isEditMode}
                    isSelfEdit={isSelfEdit}
                    userId={userId}
                    allUsers={allUsers}
                    submitting={submitting}
                    formData={formData}
                    update={update}
                    config={config}
                    nextButtonLabel={infoTabButtonLabel}
                />
            )}
            {activeTab === 2 && (
                <AssignUserTab
                    onCancel={handleCancel}
                    onSubmit={roles => handleAddUser(roles)}
                    submitting={submitting}
                    isEditMode={isEditMode}
                    initialRoles={initialRoles}
                    assignments={tab3Assignments}
                    onAssignmentsChange={setTab3Assignments}
                    formData={formData}
                    update={update}
                    customerOptions={customerOptions}
                    roleOptions={roleOptions}
                    productOptions={productOptions}
                    loading={optionsLoading}
                    hideProductDropdown={hideProductDropdown}
                    disabledRoleValues={disabledRoleValues}
                    isLoggedInAsUserManager={isLoggedInAsUserManager}
                    config={config}
                    searchingCustomers={searchingCustomers}
                    loadingMoreCustomers={loadingMoreCustomers}
                    hasMoreCustomers={hasMoreCustomers}
                    onCustomerSearch={setCustomerSearchTerm}
                    onLoadMoreCustomers={loadMoreCustomers}
                />
            )}
            </div>
        </div>
    )
}

export default UserManagement
